/**
 * TechnicianMessages Page
 *
 * List of conversations for the technician.
 * Shows channels, DMs, and groups with last message and unread count.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { FiMessageSquare, FiFolder, FiChevronRight, FiUsers, FiUser } from 'react-icons/fi';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { getConversations } from '../../../services/chatService';
import '../TechnicianPages.css';
import './ProjectChat.css';

const formatConversationTime = (timestamp) => {
  if (!timestamp) return '';
  const date = parseISO(timestamp);
  if (isToday(date)) return format(date, 'HH:mm');
  if (isYesterday(date)) return 'Ayer';
  return format(date, 'dd/MM/yy');
};

const truncateMessage = (message, maxLength = 50) => {
  if (!message) return '';
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + '...';
};

const getConversationIcon = (conversation) => {
  if (conversation.type === 'channel') return <FiFolder size={24} />;
  if (conversation.type === 'group') return <FiUsers size={24} />;
  return <FiUser size={24} />;
};

const getConversationName = (conversation) => {
  if (conversation.type === 'direct' && conversation.dmParticipant) {
    return conversation.dmParticipant.full_name || 'Usuario';
  }
  if (conversation.type === 'channel' && conversation.project) {
    return conversation.name || conversation.project.title || conversation.project.project_number;
  }
  return conversation.name || 'Conversación';
};

const TechnicianMessages = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversationsList = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getConversations();
      if (fetchError) throw fetchError;

      setConversations(data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('No se pudieron cargar las conversaciones');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchConversationsList();
  }, [fetchConversationsList]);

  const handleConversationClick = (conversationId) => {
    navigate(`/tecnico/mensajes/${conversationId}`);
  };

  const renderLastMessage = (conversation) => {
    if (!conversation.lastMessage) {
      return <span className="text-muted fst-italic">Sin mensajes</span>;
    }

    const { content, sender, message_type } = conversation.lastMessage;

    if (message_type === 'system') {
      return <span className="text-muted fst-italic">{truncateMessage(content)}</span>;
    }

    const senderName = sender?.full_name === profile?.full_name
      ? 'Tu'
      : sender?.full_name?.split(' ')[0] || 'Usuario';

    return (
      <span>
        <strong>{senderName}:</strong> {truncateMessage(content)}
      </span>
    );
  };

  if (loading) {
    return (
      <Container fluid className="technician-page">
        <div className="page-header">
          <h1 className="page-title">Mensajes</h1>
          <p className="page-subtitle">Conversaciones</p>
        </div>
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="technician-page">
        <div className="page-header">
          <h1 className="page-title">Mensajes</h1>
          <p className="page-subtitle">Conversaciones</p>
        </div>
        <Alert variant="danger" className="mb-3">{error}</Alert>
        <button className="btn btn-primary" onClick={fetchConversationsList}>Reintentar</button>
      </Container>
    );
  }

  return (
    <Container fluid className="technician-page">
      <div className="page-header">
        <h1 className="page-title">Mensajes</h1>
        <p className="page-subtitle">Conversaciones</p>
      </div>

      {conversations.length === 0 ? (
        <div className="conversations-empty">
          <FiMessageSquare className="conversations-empty-icon" />
          <h4 className="conversations-empty-title">Sin conversaciones</h4>
          <p className="conversations-empty-text">
            Los chats aparecerán aquí cuando tengas conversaciones activas
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <ul className="conversation-list">
            {conversations.map((conversation) => {
              const unread = conversation.unreadCount || 0;
              const hasUnread = unread > 0;

              return (
                <li key={conversation.id}>
                  <div
                    className="conversation-item"
                    onClick={() => handleConversationClick(conversation.id)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleConversationClick(conversation.id);
                    }}
                  >
                    <div className={`conversation-avatar ${hasUnread ? 'has-unread' : ''}`}>
                      {getConversationIcon(conversation)}
                    </div>

                    <div className="conversation-content">
                      <div className="conversation-header">
                        <span className="conversation-name">
                          {getConversationName(conversation)}
                        </span>
                        <span className={`conversation-time ${hasUnread ? 'has-unread' : ''}`}>
                          {formatConversationTime(conversation.last_message_at || conversation.created_at)}
                        </span>
                      </div>
                      <div className="conversation-preview">
                        <span className="conversation-last-message">
                          {renderLastMessage(conversation)}
                        </span>
                        {hasUnread && (
                          <span className="conversation-unread-badge">
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </div>
                    </div>

                    <FiChevronRight size={20} color="#8696a0" />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </Container>
  );
};

export default TechnicianMessages;
